import React, { useEffect, useState } from 'react';
import Select from '../../../components/Select/Select';
import Loader from '../../../components/Loader/Loader';
import { getSurveyCompletion, getStudentSurveyResult } from '../../../services/SurveyService';

const initials = (name) => name.split(' ').map((part) => part[0]).slice(0, 2).join('').toUpperCase();

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '—';
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}m ${secs}s`;
};

const SurveyAnswersTab = ({ survey, selectedStudentId, onSelectStudent }) => {
  const [completedStudents, setCompletedStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);

  useEffect(() => {
    const fetchCompleted = async () => {
      try {
        setStudentsLoading(true);
        const response = await getSurveyCompletion(survey.id, { status: 'completed' });
        if (response.status === 200) {
          const rows = response.data.data;
          setCompletedStudents(rows);
          if (!selectedStudentId && rows.length > 0) {
            onSelectStudent(rows[0].student_id);
          }
        }
      } catch (error) {
        console.error('API Error:', error.response);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchCompleted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey.id]);

  useEffect(() => {
    if (!selectedStudentId) return;
    let cancelled = false;
    const fetchResult = async () => {
      try {
        setResultLoading(true);
        const response = await getStudentSurveyResult(survey.id, selectedStudentId);
        if (!cancelled && response.status === 200) {
          setResult(response.data.data);
        }
      } catch (error) {
        console.error('API Error:', error.response);
        if (!cancelled) setResult(null);
      } finally {
        if (!cancelled) setResultLoading(false);
      }
    };
    fetchResult();
    return () => { cancelled = true; };
  }, [survey.id, selectedStudentId]);

  if (studentsLoading) {
    return (
      <div className="card survey-panel">
        <div className="survey-panel-body"><Loader /></div>
      </div>
    );
  }

  if (completedStudents.length === 0) {
    return (
      <div className="card survey-panel">
        <div className="survey-panel-body">No completed responses yet.</div>
      </div>
    );
  }

  return (
    <>
      <Select
        label="Viewing"
        name="answers-student"
        value={selectedStudentId || ''}
        options={completedStudents.map((s) => ({ id: s.student_id, name: `${s.name} (Roll ${s.roll_no})` }))}
        onChangeHandler={(e) => onSelectStudent(Number(e.target.value))}
      />

      {resultLoading || !result ? <Loader /> : (
        <>
          <div className="card survey-panel survey-answers-student-card">
            <div className="survey-panel-body survey-answers-student-row">
              <div className="survey-answers-student-info">
                <span className="survey-avatar survey-avatar-lg">{initials(result.student.name)}</span>
                <div>
                  <div className="survey-student-row-name">{result.student.name}</div>
                  <div className="survey-student-row-meta">
                    Roll {result.student.roll_no} · {result.student.id_number}
                  </div>
                </div>
              </div>
              <div className="survey-answers-stat">
                <div className="survey-answers-stat-value">{result.summary.questions_answered}/{result.summary.questions_in_survey}</div>
                <div className="survey-answers-stat-label">answered</div>
              </div>
              <div className="survey-answers-stat">
                <div className="survey-answers-stat-value">{formatDuration(result.summary.time_taken_seconds)}</div>
                <div className="survey-answers-stat-label">time taken</div>
              </div>
              <div className="survey-answers-stat">
                <div className="survey-answers-stat-value">{result.summary.submitted_at}</div>
                <div className="survey-answers-stat-label">submitted</div>
              </div>
            </div>
          </div>

          {result.answers.map((a, i) => (
            <div className="card survey-panel survey-answer-card" key={a.question_id}>
              <div className="survey-panel-body survey-answer-card-body">
                <span className="survey-answer-index">{i + 1}</span>
                <div>
                  <div className="survey-answer-question">{a.question}</div>
                  <div className="survey-answer-chips">
                    {(Array.isArray(a.answer) ? a.answer : [a.answer]).map((ans, ansIndex) => (
                      <span className="survey-answer-chip" key={`${ans}-${ansIndex}`}>{ans}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
};

export default SurveyAnswersTab;
